import React, { useEffect, useState } from "react";
import { PauseCircle, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export const AutomationSwitchPanel = () => {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState("");

  const load = async () => {
    setLoadError("");
    try { const { data } = await api.get("/admin/automation"); setStatus(data); }
    catch (e) { setLoadError(e.response?.status === 401 ? "Your admin session has expired — sign in again to see the live switch state." : (e.response?.data?.detail || "Could not load automation status")); }
  };
  useEffect(() => { load(); }, []);

  const setEnabled = async (enabled) => {
    if (enabled) {
      const typed = window.prompt("This switches ALL scheduled email jobs back on (7-day claim follow-up, onboarding nudges, digests, reminders). They will run at their next scheduled slot.\n\nType ENABLE to confirm:");
      if ((typed || "").trim() !== "ENABLE") { if (typed !== null) toast.error("Cancelled — you must type ENABLE exactly"); return; }
    } else if (!window.confirm("Pause all automated email sweeps? Nothing scheduled will send until you re-enable them.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/automation", { enabled, confirm: enabled ? "ENABLE" : "" });
      setStatus(data);
      toast.success(enabled ? "Automated email sweeps re-enabled" : "Automated email sweeps paused");
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); } finally { setBusy(false); }
  };

  // Never blank the whole section: the 7-day follow-up (Email 2) card has its own data source and must stay visible.
  if (!status) {
    return (
      <div className="border border-[#B35C4A]/50 bg-white p-5" data-testid="automation-switch-panel">
        <div className="text-[10px] uppercase tracking-widest text-[#B35C4A]">Automated email sweeps</div>
        <div className="font-serif text-xl text-[#11211A] mt-1">{loadError ? "Switch state unavailable" : "Loading switch state…"}</div>
        {loadError && (
          <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-[#B35C4A]" data-testid="automation-load-error">
            <span>{loadError}</span>
            <button type="button" onClick={load} className="btn-secondary text-xs" data-testid="automation-retry">Retry</button>
          </div>
        )}
        <div className="mt-5 border p-4 bg-white border-[#264839]/40" data-testid="followup-job-switch">
          <div className="text-[10px] uppercase tracking-widest text-[#7D8C85]">7-day claim follow-up (Email 2)</div>
          <div className="font-serif text-lg mt-0.5 text-[#7D8C85]" data-testid="followup-job-status">Switch state unknown until the status loads — stats below are live.</div>
          <FollowupReadiness />
        </div>
        <NudgeEmailPreview />
      </div>
    );
  }
  const on = status.enabled;
  const followupOff = (status.disabled_jobs || []).includes("claim_followup");
  const setJob = async (enabled) => {
    if (enabled) {
      const typed = window.prompt("Switch the 7-day claim follow-up (Email 2) back on? It will run inside the next 08:00 AEST sweep for every unclaimed supplier whose first live claim email is 7+ days old.\n\nType ENABLE to confirm:");
      if ((typed || "").trim() !== "ENABLE") { if (typed !== null) toast.error("Cancelled — you must type ENABLE exactly"); return; }
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/automation/jobs/claim_followup", { enabled, confirm: enabled ? "ENABLE" : "" });
      setStatus(data); toast.success(enabled ? "7-day follow-up enabled" : "7-day follow-up disabled");
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); } finally { setBusy(false); }
  };
  return (
    <div className={`mt-8 border-2 p-6 ${on ? "border-[#264839] bg-[#F3F7F5]" : "border-[#B35C4A] bg-[#FFF5F3]"}`} data-testid="automation-switch">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#7D8C85]">Automated email sweeps</div>
          <div className={`font-serif text-2xl mt-1 inline-flex items-center gap-2 ${on ? "text-[#264839]" : "text-[#B35C4A]"}`} data-testid="automation-status">
            {on ? <PlayCircle className="w-6 h-6" /> : <PauseCircle className="w-6 h-6" />} {on ? "ACTIVE" : "PAUSED — nothing scheduled will send"}
          </div>
          {status.updated_at && <p className="text-xs text-[#7D8C85] mt-1" data-testid="automation-updated">Last changed {status.updated_at.slice(0, 16).replace("T", " ")} UTC by {status.updated_by}</p>}
          <ul className="text-xs text-[#4A5550] mt-3 list-disc pl-5 space-y-0.5">{status.jobs.map((j) => <li key={j}>{j}</li>)}</ul>
          {status.always_on?.length > 0 && <ul className="text-xs text-[#264839] mt-2 list-disc pl-5 space-y-0.5" data-testid="automation-always-on">{status.always_on.map((j) => <li key={j}>{j}</li>)}</ul>}
          <p className="text-xs text-[#7D8C85] mt-3">Transactional emails (claim links you send, enquiry forwarding, welcome/receipt emails) are not affected. The manual "Run 7-day follow-ups" button still works when you click it.</p>
        </div>
        <button type="button" onClick={() => setEnabled(!on)} disabled={busy} className={on ? "btn-secondary text-xs disabled:opacity-50" : "btn-primary text-xs disabled:opacity-50"} data-testid="automation-toggle">
          {on ? "Pause all sweeps" : "Re-enable sweeps"}
        </button>
      </div>
      <div className={`mt-5 border p-4 bg-white ${followupOff ? "border-[#B8860B]/50" : "border-[#264839]/40"}`} data-testid="followup-job-switch">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#7D8C85]">7-day claim follow-up (Email 2)</div>
            <div className={`font-serif text-lg mt-0.5 ${followupOff ? "text-[#7A5A08]" : "text-[#264839]"}`} data-testid="followup-job-status">
              {followupOff ? "OFF — held back even when sweeps are active" : on ? "ON — runs in the daily sweep" : "ON (but all sweeps are paused)"}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Email2Preview />
            <button type="button" onClick={() => setJob(followupOff)} disabled={busy} className={followupOff ? "btn-primary text-xs disabled:opacity-50" : "btn-secondary text-xs disabled:opacity-50"} data-testid="followup-job-toggle">
              {followupOff ? "Enable 7-day follow-up" : "Disable 7-day follow-up"}
            </button>
          </div>
        </div>
        <FollowupReadiness />
      </div>
      <NudgeEmailPreview />
    </div>
  );
};

const Email2Preview = () => {
  const [open, setOpen] = useState(false);
  const [html, setHtml] = useState("");
  useEffect(() => {
    if (!open) return;
    api.get("/admin/automation/claim-followup-email-preview", { responseType: "text" }).then(({ data }) => setHtml(data)).catch(() => toast.error("Could not render Email 2 preview"));
  }, [open]);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="btn-secondary text-xs" data-testid="followup-email-preview-button">Preview Email 2</button>
      {open && (
        <div className="fixed inset-0 z-50 bg-[#11211A]/50 flex items-center justify-center p-4" onClick={() => setOpen(false)} data-testid="followup-email-preview-modal">
          <div className="bg-white w-full max-w-3xl shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#E8ECE9]">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#7D8C85]">7-day claim follow-up (Email 2)</div>
                <div className="text-sm text-[#11211A]">Exact copy suppliers receive — rendered from the live template with sample links. Nothing is sent.</div>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="btn-ghost text-xs" data-testid="followup-email-preview-close">Close</button>
            </div>
            <iframe title="Email 2 preview" srcDoc={html} sandbox="" className="w-full h-[75vh] bg-white" data-testid="followup-email-preview-frame" />
          </div>
        </div>
      )}
    </>
  );
};

const NudgeEmailPreview = () => {
  const kind = "stalled_nudge";
  const [done, setDone] = useState(2);
  const [html, setHtml] = useState("");
  const [open, setOpen] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => {
    if (!open) return;
    setErr("");
    api.get(`/admin/onboarding/nudge-email-preview?kind=${kind}&done=${done}`, { responseType: "text" })
      .then(({ data }) => setHtml(data))
      .catch(() => setErr("Could not render preview"));
  }, [kind, done, open]);
  return (
    <div className="mt-5 border border-[#264839]/40 bg-white p-4" data-testid="nudge-email-preview">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#7D8C85]">Stalled-onboarding nudge — exact email copy</div>
          <div className="font-serif text-lg mt-0.5 text-[#264839]">Read the email suppliers receive before turning the job on</div>
          <div className="text-xs text-[#7D8C85] mt-1">Rendered from the live template with a sample name; the checklist ticks match the chosen number of completed steps (the tier step is never shown to suppliers). Nothing is sent.</div>
        </div>
        <button type="button" onClick={() => setOpen((o) => !o)} className="btn-secondary text-xs" data-testid="nudge-email-preview-toggle">{open ? "Hide preview" : "Show preview"}</button>
      </div>
      {open && (
        <div className="mt-4">
          <div className="flex flex-wrap items-center gap-4 text-xs text-[#4A5550]">
            <span className="text-[#7D8C85]">Sent once per supplier, 24h after signup — no repeat nudges.</span>
            <label className="flex items-center gap-2">Steps completed
              <select value={done} onChange={(e) => setDone(Number(e.target.value))} className="border border-[#C4CFC9] px-2 py-1 bg-white" data-testid="nudge-email-preview-done">
                {[0, 1, 2].map((n) => <option key={n} value={n}>{n} of 3</option>)}
              </select>
            </label>
          </div>
          {err && <div className="text-xs text-[#B35C4A] mt-2" data-testid="nudge-email-preview-error">{err}</div>}
          <iframe title="Nudge email preview" srcDoc={html} sandbox="" className="mt-3 w-full h-[720px] bg-white border border-[#E8ECE9]" data-testid="nudge-email-preview-frame" />
        </div>
      )}
    </div>
  );
};

const FollowupReadiness = () => {
  const [r, setR] = useState(null);
  const [err, setErr] = useState(null);
  const reload = () => api.get("/admin/automation/claim-followup-readiness").then(({ data }) => setR(data)).catch((e) => setErr(e.response?.data?.detail || "Could not load readiness check"));
  useEffect(() => { reload(); }, []);
  if (err) return <p className="text-xs text-[#B35C4A] mt-3">{err}</p>;
  if (!r) return <p className="text-xs text-[#7D8C85] mt-3">Checking follow-up logic…</p>;
  const ok = r.verdict.startsWith("OK");
  return (
    <div className="mt-3 text-xs text-[#4A5550]" data-testid="followup-readiness">
      <div className={`font-medium ${ok ? "text-[#264839]" : "text-[#B35C4A]"}`} data-testid="followup-readiness-verdict">{r.verdict}</div>
      <div className="mt-1">Clock-reset repair: {r.repair.done ? `done ${String(r.repair.at || "").slice(0, 16).replace("T", " ")} UTC — ${r.repair.restored ?? 0} supplier dates restored` : "not run yet in this environment"}.
        {" "}Suppliers touched by the recovery re-send: {r.reset_by_recovery}; of those still counted “too recent”: <strong>{r.reset_by_recovery_still_too_recent}</strong>.</div>
      <div className="mt-1">If the follow-up ran now: <strong data-testid="followup-due-now">{r.stats.due_now}</strong> would receive Email 2 · {r.stats.too_recent} too recent · {r.stats.already_sent} already followed up · {r.stats.opted_out} opted out · {r.stats.bad_timestamp || 0} unreadable dates (never sent).</div>
      {r.sample_due.length > 0 && <div className="mt-1 text-[#7D8C85]">Next in line: {r.sample_due.slice(0, 5).map((s) => `${s.company || s.email} (${s.age_days}d)`).join(" · ")}</div>}
      {r.flagged?.length > 0 && (
        <div className="mt-3 border border-[#B35C4A]/40 bg-red-50 p-3" data-testid="followup-flagged">
          <div className="font-medium text-[#B35C4A]">Reset by the recovery re-send and still "too recent" ({r.flagged.length})</div>
          {r.flagged.map((f) => <FlaggedSupplier key={f.id} f={f} onDone={reload} />)}
        </div>
      )}
      <ClearByEmail onDone={reload} />
      {r.census && <PipelineCensus c={r.census} scanned={r.stats.scanned} />}
    </div>
  );
};

const CENSUS_LABELS = {
  claimed_with_link: "Claimed accounts that have a claim-link date (is_unclaimed ≠ true)",
  is_unclaimed_not_boolean: "is_unclaimed stored as text/number instead of true — query needs boolean true",
  unclaimed_link_missing: "Unclaimed, claim_link_sent_at field absent (never sent from this app)",
  unclaimed_link_null: "Unclaimed, claim_link_sent_at = null (cleared / never sent)",
  unclaimed_link_empty: "Unclaimed, claim_link_sent_at = \"\" (empty string)",
  link_but_role_not_advertiser: "Has a claim-link date but role is not 'advertiser'",
};

const PipelineCensus = ({ c, scanned }) => {
  const [open, setOpen] = useState(false);
  const excludedTotal = Object.values(c.excluded).reduce((a, b) => a + b.count, 0);
  return (
    <div className="mt-3 border border-[#C4CFC9] bg-white p-3" data-testid="followup-census">
      <button type="button" onClick={() => setOpen(!open)} className="w-full text-left flex items-center justify-between" data-testid="followup-census-toggle">
        <span className="font-medium text-[#11211A]">Why aren't more suppliers in the scan? — {c.advertisers_total} supplier accounts · {c.csv_imported} CSV-imported · <strong>{c.in_scan}</strong> in the 7-day scan · {excludedTotal} excluded</span>
        <span className="text-[#7D8C85]">{open ? "Hide" : "Show breakdown"}</span>
      </button>
      {open && (
        <div className="mt-2">
          <div className="text-[#7D8C85] italic">{c.note}</div>
          <table className="w-full mt-2 text-left" data-testid="followup-census-table">
            <tbody>
              {Object.entries(c.excluded).map(([k, v]) => (
                <tr key={k} className="border-t border-[#E3E1DA] align-top" data-testid={`followup-census-${k}`}>
                  <td className="py-1.5 pr-3 font-mono text-[11px] text-[#264839] whitespace-nowrap">{v.count}</td>
                  <td className="py-1.5 pr-3">{CENSUS_LABELS[k] || k}</td>
                  <td className="py-1.5 text-[#7D8C85]">{v.sample.map((s) => `${s.email} (${s.company || "—"}; unclaimed=${s.is_unclaimed}; link=${s.claim_link_sent_at})`).join(" · ")}</td>
                </tr>
              ))}
              <tr className="border-t border-[#E3E1DA]" data-testid="followup-census-listings-field">
                <td className="py-1.5 pr-3 font-mono text-[11px] text-[#264839]">{c.listings_with_claim_link_field}</td>
                <td className="py-1.5 pr-3" colSpan={2}>Listings (not accounts) carrying a claim_link_sent_at field — the scan reads accounts only</td>
              </tr>
              <tr className="border-t border-[#E3E1DA]" data-testid="followup-census-deleted">
                <td className="py-1.5 pr-3 font-mono text-[11px] text-[#B35C4A]">{c.deleted_accounts.last_30_days}</td>
                <td className="py-1.5 pr-3" colSpan={2}>Accounts deleted in the last 30 days ({c.deleted_accounts.unclaimed_last_30_days} were unclaimed; {c.deleted_accounts.still_restorable} still restorable in Deletion audit; methods: {Object.entries(c.deleted_accounts.by_method).map(([m, n]) => `${m} ${n}`).join(", ") || "none"})</td>
              </tr>
            </tbody>
          </table>
          <div className="mt-2 text-[#7D8C85]">Scan = role advertiser AND is_unclaimed = true AND claim_link_sent_at present. Currently scanned: {scanned}.</div>
        </div>
      )}
    </div>
  );
};

const ClearByEmail = ({ onDone }) => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    const target = email.trim();
    if (!target) return;
    if (!window.confirm(`Set claim_link_sent_at to null for ${target}? They leave the 7-day follow-up pipeline entirely (no Email 2 will be sent unless a claim link is sent again).`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/automation/claim-followup-readiness/clear-by-email", { email: target });
      setLast(data); setEmail("");
      toast.success(`Cleared claim date for ${data.email}${data.company ? ` (${data.company})` : ""}`); onDone?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Clear failed"); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="mt-3 border border-[#C4CFC9] bg-[#F7F6F2] p-3" data-testid="followup-clear-by-email">
      <div className="font-medium text-[#11211A]">Clear a supplier's claim date by email</div>
      <div className="text-[#7D8C85] mt-0.5">For test accounts (e.g. Test Co). Sets <code>claim_link_sent_at</code> to null on this environment's database and records an audit entry.</div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="supplier@example.com" className="px-3 py-1.5 text-xs border border-[#C4CFC9] bg-white min-w-[260px]" data-testid="followup-clear-email-input" />
        <button type="submit" disabled={busy || !email.trim()} className="text-xs border border-[#B35C4A] text-[#B35C4A] px-3 py-1.5 hover:bg-[#B35C4A] hover:text-white transition disabled:opacity-50" data-testid="followup-clear-email-submit">{busy ? "Clearing…" : "Clear claim date (set null)"}</button>
      </div>
      {last && <div className="mt-1 text-[#264839]" data-testid="followup-clear-email-result">Done: {last.email} — was <code>{last.from || "null"}</code>, now <code>null</code>.</div>}
    </form>
  );
};

const FlaggedSupplier = ({ f, onDone }) => {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const inspect = async () => {
    setBusy(true);
    try { const { data } = await api.get(`/admin/automation/claim-followup-readiness/inspect/${f.id}`); setInfo(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Inspect failed"); } finally { setBusy(false); }
  };
  const restore = async () => {
    if (!window.confirm(`Reset ${f.email}'s claim_link_sent_at from ${f.claim_link_sent_at} to ${info.first_live_at}?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/automation/claim-followup-readiness/restore/${f.id}`);
      toast.success(`Restored ${data.email}: ${data.from.slice(0, 16)} → ${data.to.slice(0, 16)}`); onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Restore failed"); } finally { setBusy(false); }
  };
  const clearDate = async () => {
    if (!window.confirm(`Set claim_link_sent_at to null for ${f.email}? They leave the 7-day follow-up pipeline entirely (no Email 2 will ever be sent unless a claim link is sent again).`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/automation/claim-followup-readiness/clear/${f.id}`);
      toast.success(`Cleared claim date for ${data.email}`); onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Clear failed"); } finally { setBusy(false); }
  };
  return (
    <div className="mt-2 text-xs text-[#4A5550]" data-testid={`followup-flagged-${f.id}`}>
      <div><strong>{f.company || f.email}</strong> · {f.email} · current claim_link_sent_at: <code>{f.claim_link_sent_at}</code> · recovery re-send: {String(f.safe_mode_recovery_reset_at || "").slice(0, 16)}</div>
      <div className="mt-1 flex flex-wrap items-center gap-2">
        <button type="button" onClick={inspect} disabled={busy} className="btn-secondary text-xs disabled:opacity-50" data-testid={`followup-inspect-${f.id}`}>{busy && !info ? "Reading Resend…" : "Check Resend history"}</button>
        {info && info.first_live_at && info.first_live_at < (f.claim_link_sent_at || "") && (
          <button type="button" onClick={restore} disabled={busy} className="btn-primary text-xs disabled:opacity-50" data-testid={`followup-restore-${f.id}`}>Restore original date → {info.first_live_at.slice(0, 16)}</button>
        )}
        <button type="button" onClick={clearDate} disabled={busy} className="text-xs border border-[#B35C4A] text-[#B35C4A] px-3 py-1.5 hover:bg-[#B35C4A] hover:text-white transition disabled:opacity-50" title="For test accounts: set claim_link_sent_at to null" data-testid={`followup-clear-${f.id}`}>Clear claim date (set null)</button>
      </div>
      {info && (
        <div className="mt-1">
          <div className={info.first_live_at && info.first_live_at < (f.claim_link_sent_at || "") ? "text-[#264839]" : "text-[#7A5A08]"}>{info.recommendation}</div>
          <ul className="list-disc pl-5 mt-0.5">{info.claim_emails.map((s, i) => <li key={i}>{s.at.slice(0, 16)} UTC — {s.live ? "LIVE" : "safe-mode redirected"} · {s.status}</li>)}{info.claim_emails.length === 0 && <li>No claim emails found in Resend for this address.</li>}</ul>
        </div>
      )}
    </div>
  );
};

export default AutomationSwitchPanel;
